import React, { useEffect, useMemo } from 'react';
import { Gift, Share2, X } from 'lucide-react';
import { Button } from './ui';
import { fmtXp, iconFor, tierOf } from '../utils/gamification';
import './RewardModal.css';

const COLORS = ['#a4dc00', '#c7cee6', '#f2b632', '#ffffff', '#7fa800'];

/** Confete que cai uma única vez (~4s). Some com prefers-reduced-motion. */
export function Confetti({ count = 60, seed = 7 }) {
  const pieces = useMemo(() => {
    let s = seed;
    const r = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
    return Array.from({ length: count }, (_, i) => ({
      left: `${r() * 100}%`,
      width: 6 + r() * 6,
      height: 10 + r() * 8,
      background: COLORS[i % COLORS.length],
      borderRadius: r() > 0.6 ? '50%' : 2,
      animation: `ssfall ${2.6 + r() * 2.4}s linear ${r() * 1.4}s both`,
    }));
  }, [count, seed]);
  return (
    <div className="rw-confetti" aria-hidden="true">
      {pieces.map((style, i) => <span key={i} style={style} />)}
    </div>
  );
}

/** Moldura comum: fundo escurecido, confete, painel com entrada animada, Esc e clique fora fecham. */
function RewardShell({ width, label, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ss-modal rw-modal" onClick={onClose}>
      <Confetti />
      <div
        className="ss-modal__panel rw-panel"
        style={{ maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="rw-close" onClick={onClose} aria-label="Fechar">
          <X size={18} aria-hidden="true" />
        </button>
        {children}
      </div>
    </div>
  );
}

/** Subida de nível. */
export function LevelUpModal({ rewards, courseName, onClose, onShare }) {
  const level = rewards.levelAfter;
  return (
    <RewardShell width={460} label={`Nível ${level} alcançado`} onClose={onClose}>
      <div className="rw-level__head">
        <span className="rw-eyebrow">Subiu de nível</span>
        <div className="rw-level__ring">
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle cx="60" cy="60" r="50" />
          </svg>
          <span>{level}</span>
        </div>
        <h2>Nível {level} alcançado</h2>
      </div>
      <div className="ss-modal__body">
        <p className="rw-level__text">
          Você chegou a {fmtXp(rewards.courseXp)} XP{courseName ? ` no curso ${courseName}` : ''}.
        </p>
        <div className="rw-level__next">
          <span className="rw-level__gift"><Gift size={22} aria-hidden="true" /></span>
          <span>
            <strong>Próxima meta: nível {level + 1}</strong>
            <span>Faltam {fmtXp(rewards.xpToNext)} XP. Questões, aulas e revisões contam.</span>
          </span>
        </div>
      </div>
      <div className="ss-modal__foot">
        <Button variant="outline" icon={Share2} onClick={onShare}>Compartilhar</Button>
        <Button variant="accent" onClick={onClose} autoFocus>Continuar estudando</Button>
      </div>
    </RewardShell>
  );
}

/** Medalha desbloqueada. "Compartilhar" fecha este modal e abre o de compartilhamento. */
export function MedalModal({ medal, onClose, onAlbum, onShare }) {
  const tier = tierOf(medal.tier);
  const Icon = iconFor(medal.icon);

  return (
    <RewardShell width={440} label={`Medalha desbloqueada: ${medal.name}`} onClose={onClose}>
      <div className="rw-medal" style={{ '--tier': tier.fg, '--tier-bg': tier.bg }}>
        <span className="rw-medal__shine" aria-hidden="true" />
        <span className="rw-medal__icon">
          <Icon size={56} strokeWidth={1.8} aria-hidden="true" />
        </span>
        <span className="ss-badge rw-medal__badge">Medalha {tier.label.toLowerCase()} · {medal.rarity}</span>
        <h2>{medal.name}</h2>
        <p>{medal.description}.</p>
        <div className="rw-medal__stats">
          <span><strong className="is-xp">+{medal.xp} XP</strong><span>bônus</span></span>
          <span className="rw-medal__sep" aria-hidden="true" />
          <span><strong>{medal.holdersPct}%</strong><span>dos estudantes têm</span></span>
        </div>
      </div>
      <div className="ss-modal__foot rw-medal__foot">
        <Button variant="outline" icon={Share2} onClick={onShare}>Compartilhar</Button>
        <Button onClick={onAlbum} autoFocus>Ver álbum</Button>
      </div>
    </RewardShell>
  );
}

